import { LitElement, html, css } from "lit";
import { fa } from "../../../utils/widgets.js";
import { faExpand, faCompress } from "@fortawesome/free-solid-svg-icons";

/**
 * Widget for toggling fullscreen mode on PSV or Map.
 * @class Panoramax.components.ui.widgets.Fullscreen
 * @element pnx-widget-fullscreen
 * @extends [lit.LitElement](https://lit.dev/docs/api/LitElement/)
 * @example
 * ```html
 * <pnx-widget-fullscreen _parent=${viewer} />
 * ```
 */
export default class Fullscreen extends LitElement {
	/** @private */
	static styles = css`
		pnx-button, pnx-button::part(btn) {
			width: 38px !important;
			height: 38px !important;
			max-width: 38px !important;
			max-height: 38px !important;
			min-width: unset !important;
			min-height: unset !important;
		}

		pnx-button[data-enabled="true"] {
			position: fixed;
			bottom: 10px;
			right: 10px;
		}
	`;

	/** @private */
	static properties = {
		_enabled: {state: true},
	};

	/** @private */
	constructor() {
		super();
		this._enabled = false;
	}

	/** @private */
	async _changeFullscreen(newval) {
		this._enabled = newval;

		// Enable fullscreen
		if(this._enabled) {
			const elem = this._parent;
			let reqfs = (elem.requestFullscreen || elem.webkitRequestFullScreen || elem.mozRequestFullScreen || elem.requestFullScreen)?.bind(elem);
			if(reqfs) { await reqfs(); }
			else { this._enabled = false; }
		}
		// Disable fullscreen
		else {
			try {
				const exitfs = (document.exitFullscreen || document.webkitCancelFullScreen || document.mozCancelFullScreen || document.cancelFullScreen)?.bind(document);
				if(exitfs) { await exitfs(); }
				else { this._enabled = true; }
			} catch(e) {
				if(!e.message.includes("Not in fullscreen")) {
					console.error(e);
				} else {
					this._enabled = false;
				}
			}
		}

		if(this._enabled) {
			this._parent?.grid?.toggleAway(false);
		} else {
			this._parent?.grid?.toggleAway(true);
		}
	}

	/** @private */
	render() {
		return html`
			<pnx-button
				id="pnx-zoom-in"
				size="xl"
				class="pnx-mobile-hidden pnx-print-hidden"
				kind="superflat"
				title=${this._parent?._t.pnx.zoomIn}
				data-enabled=${this._enabled}
				@click=${() => this._changeFullscreen(!this._enabled)}
			>${fa(this._enabled ? faCompress : faExpand)}</pnx-button>
		`;
	}
}

customElements.define("pnx-widget-fullscreen", Fullscreen);
